import { useState, useEffect, useCallback } from 'react'
import { UseListingDetailReturn } from './ListingDetail.types'

export const useListingDetail = (
  listingId?: number,
  loadListing?: (id: number) => Promise<any>
): UseListingDetailReturn => {
  const [listing, setListing] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const fetchListing = useCallback(
    async (id: number) => {
      if (!loadListing) {
        setError('No loadListing function provided')
        return
      }

      setLoading(true)
      setError(null)
      setListing(null)

      try {
        const data = await loadListing(id)
        setListing(data)
        setCurrentImageIndex(0)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load listing')
      } finally {
        setLoading(false)
      }
    },
    [loadListing]
  )

  useEffect(() => {
    if (listingId) {
      fetchListing(listingId)
    } else {
      setListing(null)
      setError(null)
    }
  }, [listingId, fetchListing])

  const images = listing?.urls_image || listing?.hash_images || []
  const hasImages = images.length > 0

  const goToImage = useCallback(
    (index: number) => {
      if (hasImages && index >= 0 && index < images.length) {
        setCurrentImageIndex(index)
      }
    },
    [hasImages, images.length]
  )

  const getImageUrl = useCallback((image: string) => {
    // If it's a hash, convert to full URL
    if (image && !image.startsWith('http')) {
      return `https://images.popety.io/${image}`
    }
    return image
  }, [])

  const formatPrice = useCallback((price?: number) => {
    if (!price) return '—'
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }, [])

  const formatArea = useCallback((area?: number) => {
    if (!area || area < 0) return '—'
    return `${area} m²`
  }, [])

  const formatRooms = useCallback((rooms?: number) => {
    if (!rooms || rooms < 0) return '—'
    return rooms.toString()
  }, [])

  const formatNumber = useCallback((value?: number) => {
    if (value === undefined || value === null || value < 0) return '—'
    return value.toString()
  }, [])

  const formatDate = useCallback((dateString?: string) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString('de-CH')
    } catch {
      return '—'
    }
  }, [])

  const formatDuration = useCallback((duration?: number) => {
    if (!duration) return '—'
    if (duration === 1) return '1 day'
    return `${duration} days`
  }, [])

  return {
    listing,
    loading,
    error,
    images,
    hasImages,
    currentImageIndex,
    goToImage,
    getImageUrl,
    formatPrice,
    formatArea,
    formatRooms,
    formatNumber,
    formatDate,
    formatDuration,
  }
}
